/**
 * Request body normalization — ensures route handlers receive consistent
 * body types regardless of transport (WebSocket, postMessage, WebRTC).
 *
 * After normalization:
 *   application/json  → parsed object
 *   text/*            → string
 *   binary types      → ArrayBuffer
 *   no content-type   → heuristic (JSON if it looks like JSON, else string)
 *   no body           → null
 */

/**
 * Normalize a request body to a consistent format based on content-type.
 *
 * @param {*} rawBody - Body as received from transport (string, object, ArrayBuffer, blob descriptor, etc.)
 * @param {string} [contentType] - The content-type header value
 * @returns {{ body: any, rawBody: ArrayBuffer|null }}
 */
export function normalizeBody(rawBody, contentType) {
    if (rawBody == null) {
        return { body: null, rawBody: null }
    }

    // Step 1: Convert to ArrayBuffer (canonical binary form)
    let arrayBuffer = toArrayBuffer(rawBody)

    if (arrayBuffer === null) {
        // Unknown type — pass through unchanged
        return { body: rawBody, rawBody: null }
    }

    // Step 2: Decode based on content-type
    const ct = (contentType || '').toLowerCase()

    if (ct.includes('application/json')) {
        return decodeJSON(arrayBuffer)
    }

    if (ct.includes('text/')) {
        return { body: new TextDecoder().decode(arrayBuffer), rawBody: arrayBuffer }
    }

    // No content-type or form-encoded — use heuristic
    if (!ct || ct.includes('application/x-www-form-urlencoded')) {
        return decodeHeuristic(arrayBuffer)
    }

    // Binary content-type — return ArrayBuffer as-is
    return { body: arrayBuffer, rawBody: arrayBuffer }
}

/**
 * Convert any supported input type to ArrayBuffer.
 * Returns null if the type is unrecognized.
 */
function toArrayBuffer(rawBody) {
    if (rawBody instanceof ArrayBuffer) {
        return rawBody
    }

    if (ArrayBuffer.isView(rawBody)) {
        // Int8Array from WebRTC blob descriptor, Uint8Array, Buffer from Express
        return rawBody.buffer.slice(
            rawBody.byteOffset,
            rawBody.byteOffset + rawBody.byteLength
        )
    }

    // WebRTC blob descriptor: {isBlob: true, type: string, view: Int8Array}
    if (rawBody.isBlob && rawBody.view) {
        const view = rawBody.view
        return view.buffer.slice(
            view.byteOffset,
            view.byteOffset + view.byteLength
        )
    }

    // Serialized Node.js Buffer: {type: "Buffer", data: [...]}
    // Produced when JSON.stringify is called on a Node.js Buffer, e.g. when the
    // WebSocket relay server forwards a proxied request containing binary body.
    if (rawBody.type === 'Buffer' && Array.isArray(rawBody.data)) {
        return new Uint8Array(rawBody.data).buffer
    }

    if (typeof rawBody === 'string') {
        return new TextEncoder().encode(rawBody).buffer
    }

    if (typeof rawBody === 'object') {
        // Express-parsed JSON object — round-trip through JSON
        try {
            return new TextEncoder().encode(JSON.stringify(rawBody)).buffer
        } catch {
            return null
        }
    }

    return null
}

/**
 * Try to parse ArrayBuffer as JSON, fall back to string on error.
 */
function decodeJSON(arrayBuffer) {
    try {
        const text = new TextDecoder().decode(arrayBuffer)
        return { body: JSON.parse(text), rawBody: arrayBuffer }
    } catch {
        return { body: new TextDecoder().decode(arrayBuffer), rawBody: arrayBuffer }
    }
}

/**
 * Heuristic decode for missing content-type:
 * If content looks like JSON, parse it. Otherwise return as string.
 */
function decodeHeuristic(arrayBuffer) {
    try {
        const text = new TextDecoder().decode(arrayBuffer)
        const trimmed = text.trimStart()
        if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"')) {
            return { body: JSON.parse(text), rawBody: arrayBuffer }
        }
        return { body: text, rawBody: arrayBuffer }
    } catch {
        // JSON-shaped but invalid JSON — return as string
        return { body: new TextDecoder().decode(arrayBuffer), rawBody: arrayBuffer }
    }
}
